/* eslint-disable no-param-reassign */
import React from 'react';

import { Editor } from '@tinymce/tinymce-react';
import './TinymceEditor.scss';
import { getToolbarConfig } from './utils/toolbarConfig';
import { registerAiFeatures } from './utils/aiConfig';

const MAX_INLINE_IMAGE_SIZE_BYTES = 512 * 1024; // 0.5MB in bytes

const TinymceEditor = ({
  initialValue,
  imageUploadHandler,
  handleChange,
  value,
  height = '300px',
  disabled = false,
  notDisaplayTools = '',
  autofocus,
  onBlur,
  scrollIntoView,
  disableInlineImages,
  testid,
  onAiButtonClick,
}) => {
  function handleInit(evt, editor) {
    // scroll to make the toolbar visible
    if (scrollIntoView) {
      setTimeout(() => {
        editor
          ?.getContainer()
          ?.querySelector('.tox-editor-container')
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }

  return (
    <Editor
      disabled={disabled}
      data-testid={testid}
      onInit={handleInit}
      tinymceScriptSrc="https://turmericp.liscio.me/tinymce/js/tinymce/tinymce.min.js"
      initialValue={initialValue}
      value={value}
      onBlur={onBlur}
      init={{
        images_replace_blob_uris: false,
        convert_urls: false,
        paste_data_images: !disableInlineImages,
        auto_focus: autofocus,
        height,
        menubar: false,
        paste_retain_style_properties: 'all',
        paste_webkit_styles: 'all',
        paste_convert_word_fake_lists: false,
        // open embedded link in new tab
        link_target_list: [{ title: 'New window', value: '_blank' }],
        contextmenu: false,
        paste_postprocess(plugin, args) {
          args.node.querySelectorAll('a').forEach((anchorTag) => {
            if (!anchorTag.innerHTML) {
              anchorTag.remove();
            }
          });
        },
        plugins: [
          'link',
          'autolink',
          'hr',
          'lists',
          'media',
          'advlist',
          'image',
          'quickbars',
        ],
        browser_spellcheck: true,
        toolbar: getToolbarConfig(
          notDisaplayTools,
          disableInlineImages,
          onAiButtonClick,
        ),
        content_style: `@import url('https://fonts.googleapis.com/css?family=Source+Sans+Pro:300,400,600,700');
        body {
          font-family:Source Sans Pro, sans-serif;
          font-size:0.94rem;
          color: rgb(33, 43, 54);
          margin:0;
          padding: 1px 0.5rem;
        }

        p {
          color: rgb(33, 43, 54);
          margin: 0px;
        }

        #tinymce * {
          text-indent: 0 !important;
        }

        .mce-item-table, .mce-item-table td, .mce-item-table tr {
          border: inherit !important;
          outline: inherit !important;
        }

        .mce-content-body {
          min-height:210px;
        }`,
        quickbars_selection_toolbar: false,
        link_assume_external_targets: true,
        toolbar_location: 'bottom',
        quickbars_insert_toolbar: false,
        quickbars_image_toolbar: false,
        elementpath: false,
        branding: false,
        resize: true,
        autoresize: true,
        images_file_types: 'jpg,png,jpeg',
        image_description: false,
        image_dimensions: false,
        image_title: false,
        a11y_advanced_options: true,
        images_upload_handler: (blobInfo, progress) => {
          if (blobInfo.blob().size > MAX_INLINE_IMAGE_SIZE_BYTES) {
            const error = new Error(
              'Image size exceeds the maximum allowed size of 0.5MB',
            );
            error.remove = true;
            return Promise.reject(error);
          }

          // Use the provided imageUploadHandler if available
          if (imageUploadHandler) {
            return imageUploadHandler(blobInfo, progress);
          }

          // Default handler if no custom handler is provided
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blobInfo.blob());
          });
        },
        object_resizing: true,
        forced_root_block: 'div',
        init_instance_callback: (editor) => {
          editor.on('GetContent', (e) => {
            if (e.content.includes('a')) {
              const editImage = e.content;
              const newString = editImage.replace(
                /<a/g,
                '<a style="text-decoration: underline"',
              );
              e.content = newString;
            }
          });
          editor.on('ObjectResized', (ev) => {
            ev.target.dataset.mceStyle =
              'style="max-width: none; max-height: none"';
          });
        },
        setup(editor) {
          // Register AI features conditionally based on onAiButtonClick
          registerAiFeatures(editor, onAiButtonClick);

          editor.ui.registry.addContextMenu('useBrowserSpellcheck', {
            update() {
              return editor.selection.isCollapsed()
                ? ['useBrowserSpellcheck']
                : [];
            },
          });

          // Add validation for inline image size (0.5MB limit)
          editor.on('paste', (e) => {
            const { clipboardData } = e;

            if (clipboardData && clipboardData.items) {
              let hasOversizedImage = false;

              Array.from(clipboardData.items).forEach((item) => {
                if (item.type.indexOf('image') !== -1) {
                  const blob = item.getAsFile();
                  if (blob && blob.size > MAX_INLINE_IMAGE_SIZE_BYTES) {
                    e.preventDefault();
                    hasOversizedImage = true;
                  }
                }
              });

              // Display notification only once if any oversized images were found
              if (hasOversizedImage) {
                editor.notificationManager.open({
                  text: 'Image size exceeds the maximum allowed size of 0.5MB',
                  type: 'error',
                  timeout: 3000,
                });
              }
            }
          });
        },
      }}
      onEditorChange={handleChange}
    />
  );
};

export default TinymceEditor;
